import type { AiCompanionTrainingReviewModel } from "@simbelapp/order-sdk";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useTrainingPrefillStore } from "~/store/copilot/training-prefill.store";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import { isImportableProgramUrl, trainingPrefillOf } from "~/utils/helpers/copilot/training-review";

/**
 * Bridges the `reviewTraining` tool output and the training form. Its output — a draft training
 * (create) or a diff on an existing one (update) — is the review itself, but the form is never
 * opened on it automatically: the answer ends on a CTA and the admin decides when to leave the chat.
 */
export const useTrainingPrefillImport = () => {
  const prefillStore = useTrainingPrefillStore();
  const createTrainingStore = useCreateTrainingStore();

  /** The URL the user submitted for import — null when the source was a file. */
  const pendingSourceUrl = ref<string | null>(null);

  /** Opens the form on a review — driven by the chat card's CTA, the only way in. */
  async function openReview(review: AiCompanionTrainingReviewModel) {
    /*
     * A form the admin opened themselves is theirs, edits included, so the review waits for them to
     * close it. The assistant's own pre-filled form is not: pressing the CTA again is asking for
     * that same form back, and `openImportedTraining` resets it before writing the review into it.
     */
    if (!prefillStore.isActive && (createTrainingStore.showModal || createTrainingStore.showExit)) {
      useFeedback().error("Fermez la fiche formation ouverte avant d'ouvrir celle-ci", "small");

      return;
    }

    try {
      const prefill = trainingPrefillOf(review);
      prefill.sourceUrl = pendingSourceUrl.value;
      /*
       * No navigation, and the panel is left open: the form is hosted at the root for every page
       * that does not mount it itself (`app.vue`), so it opens over whatever the admin was doing
       * and leaves them — and their conversation — where they were.
       */
      await prefillStore.openImportedTraining(prefill);
    } catch {
      createTrainingStore.$reset();
      prefillStore.reset();
      useFeedback().error("La fiche formation n'a pas pu être ouverte", "small");
    }
  }

  function setSourceUrl(url: string | null) {
    // A URL the viewer could not show safely is refused outright: the previous one stands.
    if (url && !isImportableProgramUrl(url)) return;

    pendingSourceUrl.value = url;
  }

  function reset() {
    pendingSourceUrl.value = null;
  }

  return { openReview, setSourceUrl, reset };
};
